// web/src/registry/features/_part-3.ts —— FEATURES 的第 3/6 段（12 个元素：quality / knowledge / ticketing）
// 拆自 web/src/registry/features.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { Feature } from '../features';

export const FEATURES_PART_3: Feature[] = [

  {
    id: 'quality.analytics',
    title: '质检分析',
    domain: 'quality',
    scope: 'domain',
    type: 'analytics',
    route: '/quality/analytics',
    legacyLocations: ['/quality?tab=stats'],
    permission: 'quality.analytics',
    nav: { section: 'business', group: 'quality', order: 6 },
    priority: 'P1',
  },

  // 知识库（WeKnora 为唯一知识来源）：本域只剩三个侧栏条目——
  // 知识问答 / 知识管理（WeKnora 文档库管理面）/ 问答历史。
  // 自建的问答库与全文库、以及依赖它们的应答链已整体下线：
  // 页面、路由、权限码、旧地址映射一并删除，老地址不再重定向（落到 404）。
  {
    // 知识管理（WeKnora 文档型知识库）：管外部系统的知识库/目录/文档，
    // 也就是「知识问答」所依赖的原文库。**独立菜单码 knowledge.kbmanage**：
    // 它原先复用「问答库管理」那枚菜单码，那枚码随问答库下线后不能再用，
    // 否则唯一保留的 WeKnora 入口会跟着断掉。
    id: 'knowledge.kbmanage',
    title: '知识管理',
    domain: 'knowledge',
    scope: 'domain',
    type: 'resource',
    route: '/knowledge/kb',
    permission: 'knowledge.kbmanage',
    nav: { section: 'business', group: 'knowledge', order: 2 },
    priority: 'P1',
    issue: '外部系统嵌入：知识库/文档/标签的管理面都在 WeKnora（/weknora/platform/knowledge-bases），BCC 只提供菜单与权限（登录态用本系统 token 同源注入，不再换票）；页内不消费本系统业务端点',
  },
  {
    // 知识问答（WeKnora 嵌入）：页面只有一个 iframe 壳，内容与数据都在外部系统，
    // 本系统只提供菜单与权限（登录态用本系统 token 同源注入，不再让用户登第二次）。
    id: 'knowledge.chat',
    title: '知识问答',
    domain: 'knowledge',
    scope: 'domain',
    type: 'workspace',
    route: '/knowledge/chat',
    permission: 'knowledge.chat',
    nav: { section: 'business', group: 'knowledge', order: 1 },
    priority: 'P1',
    issue: '外部系统嵌入：页内不消费本系统端点（登录态用本系统 token 同源注入，不走换票）；WeKnora 是唯一知识来源，口径边界见 .local/weknora-embed-assessment.md §7',
  },
  {
    // 问答历史：同一外部系统的会话列表。单独占一个条目而不是做成「知识问答」的页内页签，
    // 因为外部系统没有独立的历史路由（历史是它对话页的侧栏），嵌入时必须自己给一个落地页。
    id: 'knowledge.chat.history',
    title: '问答历史',
    domain: 'knowledge',
    scope: 'domain',
    type: 'record',
    route: '/knowledge/chat/history',
    permission: 'knowledge.chat.history',
    nav: { section: 'business', group: 'knowledge', order: 3 },
    priority: 'P1',
    issue: '外部系统嵌入：会话按「本系统用户」隔离（每人一个外部账号，JIT 建档），不是全员共享一份；不能按本系统组织做数据分权（外部系统只有单空间）',
  },
  {
    // 反馈处理：同一外部系统的点踩处理面（坐席点踩 → 班长处理 → 结论回写坐席）。
    // 单独一个菜单条目而不是并进「知识问答」：看的是全租户的点踩意见，
    // 是管理动作，与坐席查资料的公共面不是同一批人、也不是同一批数据。
    id: 'knowledge.feedback',
    title: '反馈处理',
    domain: 'knowledge',
    scope: 'domain',
    type: 'workspace',
    route: '/knowledge/feedback',
    permission: 'knowledge.feedback',
    nav: { section: 'business', group: 'knowledge', order: 4 },
    priority: 'P1',
    issue: '外部系统嵌入：处理单与回答都在 WeKnora（/weknora/platform/feedback），BCC 只提供菜单与权限；页面只对管理员开放，坐席侧的点踩入口在「知识问答」卡片里',
  },

  // 客户与工单
  {
    id: 'ticketing.customers',
    title: '客户',
    domain: 'ticketing',
    scope: 'domain',
    type: 'resource',
    route: '/crm/customers',
    legacyLocations: ['/crm'],
    permission: 'ticketing.customers',
    nav: { section: 'business', group: 'ticketing', order: 1 },
    priority: 'P1',
    issue: '坐席处处使用客户却无入口；是否开放 agent 只读待确认',
  },
  {
    id: 'ticketing.tickets',
    title: '工单',
    domain: 'ticketing',
    scope: 'domain',
    type: 'workspace',
    route: '/tickets',
    legacyLocations: ['/tickets'],
    permission: 'ticketing.tickets',
    nav: { section: 'business', group: 'ticketing', order: 2 },
    priority: 'P2',
  },
  {
    id: 'ticketing.settings.fields',
    title: '工单字段与派单',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings/fields',
    legacyLocations: ['/system/ticket-fields'],
    permission: 'ticketing.settings.fields',
    priority: 'P0',
    issue: '业务配置埋在 /system',
  },
  {
    id: 'ticketing.settings.templates',
    title: '工单创建模板',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings/templates',
    legacyLocations: ['/system/ticket-templates'],
    permission: 'ticketing.settings.templates',
    priority: 'P0',
    issue: '业务配置埋在 /system',
  },
  {
    id: 'ticketing.settings.routing',
    title: '派单规则',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings/routing',
    legacyLocations: ['/system/ticket-fields?tab=rules'],
    permission: 'ticketing.settings.routing',
    priority: 'P1',
  },
  {
    id: 'ticketing.settings.approval',
    title: '工单审核流程',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings/approval',
    legacyLocations: ['/system/workflows'],
    permission: 'ticketing.settings.approval',
    priority: 'P0',
    issue: '审批流引擎属平台（workflow.approvals），工单侧只保留“用哪条审批流”的绑定',
  },
  {
    id: 'ticketing.settings.autocreate',
    title: 'AI 自动建单',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings/auto-create',
    legacyLocations: ['/aicenter'],
    permission: 'ticketing.settings.auto-create',
    priority: 'P0',
    issue: '修改对象是 Ticket，配置却放在 AI 中台（名字含 AI 但业务对象是工单）',
  },
  {
    id: 'ticketing.settings',
    title: '客户与工单设置',
    domain: 'ticketing',
    scope: 'domain',
    type: 'config',
    route: '/tickets/settings',
    // 设置 Landing：任一子配置权限即可见（当前仅 admin 持有），页内 Tab 按各自子码裁剪
    permission: [
      'ticketing.settings', 'ticketing.settings.fields', 'ticketing.settings.templates',
      'ticketing.settings.routing', 'ticketing.settings.approval', 'ticketing.settings.auto-create',
    ],
    nav: { section: 'business', group: 'ticketing', order: 6 },
    priority: 'P1',
    issue: 'Settings Landing：聚合字段/模板/派单/审核/AI 自动建单',
  },];
